import { useMemo } from "react";

/**
 * Every figure that belongs to a question, whichever part it was printed
 * under.
 *
 * A paper's rows are flat: the parts of question 4 are the rows whose
 * `number` is 4, and each row carries its figures as `image_0`, `image_1`
 * and so on. A student answering 4 b) ii) often needs the diagram printed
 * under 4 b), so the list is gathered per question number rather than per
 * part — that mismatch is what made the old "upload to" flow confusing.
 *
 * The loaded images come from the map SolvePaper already keeps, so nothing is
 * fetched twice and the thumbnails are the same objects the canvas draws.
 */
const MAX_FIGURES_PER_ROW = 10;

export function useQuestionFigures({ questions, questionNumber, images, partLabelOf }) {
  return useMemo(() => {
    if (!questions || questionNumber == null) return [];

    const found = [];
    questions
      .filter((q) => q.number === questionNumber)
      .forEach((row) => {
        for (let i = 0; i < MAX_FIGURES_PER_ROW; i += 1) {
          const key = `image_${i}`;
          if (!row[key]) continue;
          const loaded = images?.[`${row.guid}_${key}`];
          if (!loaded) continue; // still loading: it appears when it arrives
          found.push({
            id: `${row.guid}_${key}`,
            // Named by the part it was printed under, so a student can tell
            // two diagrams apart without opening them.
            label: partLabelOf ? partLabelOf(row) : `Figure ${found.length + 1}`,
            image: loaded,
            width: loaded.naturalWidth || loaded.width,
            height: loaded.naturalHeight || loaded.height,
          });
        }
      });
    return found;
  }, [questions, questionNumber, images, partLabelOf]);
}
